import { PropsWithChildren, useEffect, useReducer } from 'react';
import Cookies from 'js-cookie';
import { OrganizationUserDetailInfo, UserDetailInfo } from '@/types';
import { AuthContext, AuthState, initialState, Action, ActionKind } from './Context';
import { getUserInfo, parseCode } from '@/api/api';
import { useEvent } from '@/hooks/useEvent';

const { VITE_FELO_API_URL } = import.meta.env;

const reducer = (state: AuthState, action: Action): AuthState => {
  switch (action.type) {
    case ActionKind.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };
    case ActionKind.IS_LOGIN:
      return {
        ...state,
        isLogin: action.payload,
      };
    case ActionKind.LOGIN:
      return {
        ...state,
        user: action.payload.user,
      };
    case ActionKind.LOGOUT:
      return {
        ...initialState,
      };
    case ActionKind.UPDATE_USER:
      return {
        ...state,
        user: action.payload,
      };
    default:
      return state;
  }
};

export const makeUserInfo = (
  userInfo: any,
  accountInfo: OrganizationUserDetailInfo,
): UserDetailInfo => ({
  openId: accountInfo.id,
  userId: userInfo.userId,
  deptIds: accountInfo.dept_ids || [],
  type: accountInfo.type,
  userName: userInfo.userName || accountInfo.user_name,
  displayName: accountInfo.display_name,
  avatar: userInfo.userLogo || accountInfo.avatar,
  gender: accountInfo.gender,
  mobile: accountInfo.mobile,
  phone: accountInfo.phone,
  email: userInfo.email,
  personalStatus: accountInfo.personal_status,
  deleted: accountInfo.deleted,
  version: accountInfo.version,
  isAnonymous: userInfo.userChannel === 'ANONYMOUS',
  profile: userInfo.profile,
  userNumberId: userInfo.userNumberId,
  device: userInfo.device,
  teamId: accountInfo.team_id,
});

const getInitialState = (): AuthState => ({
  ...initialState,
});

export function AuthProvider({ children }: PropsWithChildren<unknown>) {
  const [state, dispatch] = useReducer(reducer, initialState, getInitialState);

  // 清理URL中的hash和isNew参数
  const cleanupUrl = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const isNew = searchParams.get('isNew');
    let needCleanup = false;
    let currentUrl = window.location.href;

    // 移除hash
    if (window.location.hash) {
      currentUrl = currentUrl.split('#')[0];
      needCleanup = true;
    }

    // 移除isNew参数
    if (isNew) {
      currentUrl = currentUrl.replace(/[?&]isNew=[^&]*/g, '');
      currentUrl = currentUrl.replace(/\?$/, '').replace(/\?&/, '?').replace(/&&/g, '&');
      needCleanup = true;
    }

    if (needCleanup) {
      window.history.replaceState(null, '', currentUrl);
    }
  };

  const login = useEvent(async () => {
    try {
      // 如果URL中有code，尝试解析
      if (window.location.hash) {
        const fullHash = window.location.hash;
        const hashWithoutSymbol = fullHash.slice(1);

        try {
          const { data: { data: token } } = await parseCode(hashWithoutSymbol);

          Cookies.set(import.meta.env.VITE_COOKIE_SUFFIX as string, token, {
            expires: 30,
            domain: import.meta.env.VITE_DOMAIN,
          });
        } catch (parseError) {
          // parseCode失败，但不要急着跳转登录
          // 继续往下走，检查是否已有有效登录态
          console.warn('[AuthProvider] parseCode失败，将检查现有登录态:', parseError);
        }

        // 清理URL参数（不管parseCode成功还是失败都要清理）
        cleanupUrl();
      }

      // 检查登录态（不管parseCode成功还是失败，都用cookie中的token检查）
      dispatch({ type: ActionKind.SET_LOADING, payload: true });
      const { data } = await getUserInfo();
      if (data.status === 200) {
        // 已有有效登录态，直接使用（不管是否和app端用户一致）
        dispatch({ type: ActionKind.IS_LOGIN, payload: true });
        dispatch({ type: ActionKind.SET_LOADING, payload: false });
        dispatch({
          type: ActionKind.LOGIN,
          payload: {
            user: data.data,
          },
        });
      } else {
        // 没有有效登录态，才跳转登录
        dispatch({ type: ActionKind.IS_LOGIN, payload: false });
        dispatch({ type: ActionKind.SET_LOADING, payload: false });
        skiplogin();
      }
    } catch (error) {
      // 出错了，跳转登录
      dispatch({ type: ActionKind.IS_LOGIN, payload: false });
      dispatch({ type: ActionKind.SET_LOADING, payload: false });
      skiplogin();
    }
  });

  const logout = useEvent(() => {


    dispatch({ type: ActionKind.LOGOUT });
  });
  const skiplogin = () => {
    const url = new URL(`${VITE_FELO_API_URL}/login`);
    url.searchParams.append('app_id', 'webtranslation');

    // 构建干净的redirect_uri（移除hash和isNew参数，避免参数累加导致循环跳转）
    let redirectUri = window.location.href;
    redirectUri = redirectUri.split('#')[0];
    redirectUri = redirectUri.replace(/[?&]isNew=[^&]*/g, '');
    redirectUri = redirectUri.replace(/\?$/, '').replace(/\?&/, '?').replace(/&&/g, '&');

    // 将 redirect_uri 放在 hash 中以避免 URL 过长导致 414 错误
    url.hash = `redirect_uri=${encodeURIComponent(redirectUri)}`;
    window.location.href = url.toString();
  };

  useEffect(() => {
    login();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider
      value={{ state, dispatch, login, logout, skiplogin }}
    >
      {
        state.isLogin ? <>{children}</> : <></>
      }

    </AuthContext.Provider>
  );
}
